import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// Types
interface GeneratedText {
  content: string;
  prompt: string;
  timestamp: Date;
  format: string;
  fileName: string;
}

// File format options with proper formatting functions and MIME types
const FILE_FORMATS = [
  { 
    value: 'txt', 
    label: 'Text File (.txt)', 
    icon: '📄',
    ext: '.txt',
    mime: 'text/plain'
  },
  { 
    value: 'json', 
    label: 'JSON File (.json)', 
    icon: '🔧',
    ext: '.json',
    mime: 'application/json',
    formatContent: (content: string) => {
      try {
        return JSON.stringify(JSON.parse(content), null, 2);
      } catch {
        return JSON.stringify({ content }, null, 2);
      }
    }
  },
  { 
    value: 'html', 
    label: 'HTML File (.html)', 
    icon: '🌐',
    ext: '.html',
    mime: 'text/html',
    formatContent: (content: string) => {
      if (!content.includes('<!DOCTYPE html>')) {
        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Generated Content</title>
</head>
<body>
    ${content}
</body>
</html>`;
      }
      return content;
    }
  },
  { 
    value: 'md', 
    label: 'Markdown (.md)', 
    icon: '📑',
    ext: '.md',
    mime: 'text/markdown'
  },
  { 
    value: 'py', 
    label: 'Python (.py)', 
    icon: '🐍',
    ext: '.py',
    mime: 'text/x-python',
    formatContent: (content: string) => {
      if (!content.includes('"""')) {
        return `"""
${content}
"""\n\n# Your Python code here\n`;
      }
      return content;
    }
  },
  { 
    value: 'js', 
    label: 'JavaScript (.js)', 
    icon: '📜',
    ext: '.js',
    mime: 'text/javascript',
    formatContent: (content: string) => {
      if (!content.includes('/*')) {
        return `/*
${content}
*/\n\n// Your JavaScript code here\n`;
      }
      return content;
    }
  },
  { 
    value: 'java', 
    label: 'Java (.java)', 
    icon: '☕',
    ext: '.java',
    mime: 'text/x-java-source',
    formatContent: (content: string) => {
      if (!content.includes('/*')) {
        return `/*
${content}
*/\n\npublic class Main {\n    public static void main(String[] args) {\n        // Your Java code here\n    }\n}\n`;
      }
      return content;
    }
  },
  { 
    value: 'cpp', 
    label: 'C++ (.cpp)', 
    icon: '⚡',
    ext: '.cpp',
    mime: 'text/x-c++src',
    formatContent: (content: string) => {
      if (!content.includes('/*')) {
        return `/*
${content}
*/\n\n#include <iostream>\n\nint main() {\n    // Your C++ code here\n    return 0;\n}\n`;
      }
      return content;
    }
  },
  { 
    value: 'cs', 
    label: 'C# (.cs)', 
    icon: '🎯',
    ext: '.cs',
    mime: 'text/x-csharp',
    formatContent: (content: string) => {
      if (!content.includes('/*')) {
        return `/*
${content}
*/\n\nusing System;\n\nclass Program {\n    static void Main() {\n        // Your C# code here\n    }\n}\n`;
      }
      return content;
    }
  },
  { 
    value: 'sql', 
    label: 'SQL (.sql)', 
    icon: '🗄️',
    ext: '.sql',
    mime: 'text/x-sql',
    formatContent: (content: string) => {
      if (!content.includes('--')) {
        return `-- ${content.split('\n').join('\n-- ')}\n\n`;
      }
      return content;
    }
  }
];

// Utility function to format content based on file type
const formatContentForFileType = (content: string, format: string): string => {
  const formatType = FILE_FORMATS.find(f => f.value === format);
  if (formatType?.formatContent) {
    return formatType.formatContent(content);
  }
  return content;
};

// Get proper MIME type for file format
const getMimeType = (format: string): string => {
  const formatType = FILE_FORMATS.find(f => f.value === format);
  return formatType?.mime || 'text/plain';
};

// Utility Functions
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

// Action button component for consistent styling
const ActionButton: React.FC<{
  onClick: () => void;
  title: string;
  color: string;
  children: React.ReactNode;
}> = ({ onClick, title, color, children }) => (
  <button
    onClick={onClick}
    className={`p-2 bg-${color}-500/20 hover:bg-${color}-500/40 rounded-lg text-${color}-400 hover:text-${color}-300 transition-colors`}
    title={title}
  >
    {children}
  </button>
);

// Icons
const Icons = {
  Download: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  Copy: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
    </svg>
  ),
  Delete: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  )
};

// Components
const Header: React.FC<{
  showHistory: boolean;
  onToggleHistory: () => void;
}> = ({ showHistory, onToggleHistory }) => (
  <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center space-x-4">
          <Link
            to="/generation"
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-3 w-3 bg-blue-500 rounded-full animate-pulse" />
            <h1 className="text-xl font-bold text-white">Text Generation</h1>
          </div>
        </div>

        <button
          onClick={onToggleHistory}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            showHistory
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
          title={showHistory ? 'Hide History' : 'Show History'}
        >
          <ClockIcon className="w-5 h-5" />
          <span className="text-sm font-medium">History</span>
        </button>
      </div>
    </div>
  </div>
);

const InputForm: React.FC<{
  prompt: string;
  format: string;
  loading: boolean;
  onPromptChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFormatChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}> = ({ prompt, format, loading, onPromptChange, onFormatChange, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <textarea
      value={prompt}
      onChange={onPromptChange}
      placeholder="Enter your text prompt..."
      className="w-full h-32 p-3 bg-gray-800 text-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none"
    />
    <div className="flex gap-2">
      <select
        value={format}
        onChange={onFormatChange}
        className="bg-gray-800 text-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
      >
        <option value="">Select format</option>
        {FILE_FORMATS.map(format => (
          <option key={format.value} value={format.value}>
            {format.icon} {format.label}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={loading || !prompt.trim() || !format}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Generating...' : 'Generate'}
      </button>
    </div>
  </form>
);

const TextCard: React.FC<{
  item: GeneratedText;
  onDelete: () => void;
  onCopy: () => void;
  onDownload: () => void;
}> = ({ item, onDelete, onCopy, onDownload }) => {
  const formatInfo = FILE_FORMATS.find(f => f.value === item.format);
  
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-transform hover:scale-[1.02] hover:shadow-xl">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {formatDate(item.timestamp)}
          </span>
          <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
            {formatInfo?.icon} .{item.format}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton onClick={onDownload} title="Download file" color="green">
            <Icons.Download />
          </ActionButton>
          <ActionButton onClick={onCopy} title="Copy text" color="blue">
            <Icons.Copy />
          </ActionButton>
          <ActionButton onClick={onDelete} title="Delete text" color="red">
            <Icons.Delete />
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

const HistorySidebar: React.FC<{
  show: boolean;
  items: GeneratedText[];
  onClose: () => void;
  onDelete: (index: number) => void;
  onCopy: (text: string) => void;
  onDownload: (item: GeneratedText) => void;
}> = ({ show, items, onClose, onDelete, onCopy, onDownload }) => (
  <div className={`fixed top-0 right-0 h-full w-96 bg-gray-900 border-l border-gray-700 transform transition-transform ${show ? 'translate-x-0' : 'translate-x-full'}`}>
    <div className="p-4 border-b border-gray-700 flex justify-between items-center">
      <h2 className="text-lg font-semibold text-gray-200">History</h2>
      <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
        <XMarkIcon className="w-6 h-6" />
      </button>
    </div>
    <div className="p-4 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 4rem)' }}>
      {items.map((item, index) => (
        <TextCard
          key={item.fileName}
          item={item}
          onDelete={() => onDelete(index)}
          onCopy={() => onCopy(item.content)}
          onDownload={() => onDownload(item)}
        />
      ))}
    </div>
  </div>
);

// Main Component
const TextGeneration: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GeneratedText[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || !format || loading) return;

    setLoading(true);
    try {
      const formattedContent = formatContentForFileType(prompt.trim(), format);
      const formatType = FILE_FORMATS.find(f => f.value === format);
      const fileName = `nexon-generated_${format}_${Date.now()}${formatType?.ext || '.txt'}`;
      
      const newItem: GeneratedText = {
        content: formattedContent,
        format,
        fileName,
        prompt: prompt.trim(),
        timestamp: new Date()
      };
      
      setItems(prev => [newItem, ...prev]);
      setPrompt('');
      setFormat('');
    } catch (err) {
      console.error('Error generating text:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (content: string, format: string, fileName: string) => {
    try {
      const mimeType = getMimeType(format);
      const formattedContent = formatContentForFileType(content, format);
      const blob = new Blob([formattedContent], { type: mimeType });
      const element = document.createElement('a');
      element.href = URL.createObjectURL(blob);
      element.download = fileName;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(element.href);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200">
      <Header showHistory={showHistory} onToggleHistory={() => setShowHistory(!showHistory)} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">AI Text Generation</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Generate text content in various file formats using our advanced AI model.
            Choose your desired format and let AI handle the content generation.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-8">
          <InputForm
            prompt={prompt}
            format={format}
            loading={loading}
            onPromptChange={(e) => setPrompt(e.target.value)}
            onFormatChange={(e) => setFormat(e.target.value)}
            onSubmit={handleSubmit}
          />

          {/* Results Section */}
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={item.fileName} className="bg-gray-800 rounded-lg overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {formatDate(item.timestamp)}
                    </span>
                    <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                      {FILE_FORMATS.find(f => f.value === item.format)?.icon} .{item.format}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ActionButton onClick={() => handleDownload(item.content, item.format, item.fileName)} title="Download file" color="green">
                      <Icons.Download />
                    </ActionButton>
                    <ActionButton onClick={() => handleCopy(item.content)} title="Copy text" color="blue">
                      <Icons.Copy />
                    </ActionButton>
                    <ActionButton onClick={() => setItems(items.filter((_, i) => i !== index))} title="Delete text" color="red">
                      <Icons.Delete />
                    </ActionButton>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4 space-y-3">
                  <div className="text-sm text-gray-400">
                    <span className="font-medium text-gray-300">Prompt:</span> {item.prompt}
                  </div>
                  <div className="bg-gray-900 p-4 rounded-lg">
                    <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
                      {item.content}
                    </pre>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty State */}
            {items.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 mb-4">
                  <ClockIcon className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-300 mb-2">No text generated yet</h3>
                <p className="text-gray-500">Start by entering a prompt and choosing a format</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <HistorySidebar
        show={showHistory}
        items={items}
        onClose={() => setShowHistory(false)}
        onDelete={(index) => setItems(items.filter((_, i) => i !== index))}
        onCopy={handleCopy}
        onDownload={(item) => handleDownload(item.content, item.format, item.fileName)}
      />
    </div>
  );
};

export default TextGeneration;
